import React, { memo, useMemo, useState } from 'react';
import {
  Badge,
  useBalloonEventsMetadata,
  useTranslations,
  useQuery,
} from 'react-ui';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/free-mode';

import { FilterLabelBadge } from './FilterLabelBadge';
import { useBalloonEventsOptions } from '../contexts/EmbedOptions';
import { FilterLabel } from '../types/FilterLabel';
import { BalloonEventsQuery } from 'ts-core';
import { trackMixpanel } from '../utils/mixpanel';
import { useIsMobile } from "react-ui";
export interface FilterLabelCarouselProps {
  className?: string;
}
export const FilterLabelCarousel = memo(function FilterLabelCarousel({
  className,
}: FilterLabelCarouselProps) {
  const { addQuery, removeQuery, has, queries, removeQueryById } = useQuery();
  const { translated } = useTranslations();
  const { metadata } = useBalloonEventsMetadata();
  const isMobile = useIsMobile();
  const { language } = useTranslations();

  const { filterLabels } = useBalloonEventsOptions();
  const [isDrag, setDrag] = useState(false);
  const allFilterLabels = useMemo(() => {
    const categoryToFilterLabel = (category: any): FilterLabel => {
      const children = (category.children || []).map((child: any) =>
        categoryToFilterLabel(child)
      );
      return {
        label: category.displayName.languageTexts,
        color: category.color,
        icon: category.icon,
        query: { id: category.id, categories: [category.id] },
        children: children.length > 0 ? children : undefined,
      };
    };

    if (filterLabels) {
      const newFilterLabels: FilterLabel[] = [];
      if (filterLabels.custom) {
        filterLabels.custom.forEach((c, i) => {
          if (!c.filter.query.id) {
            c.filter.query.id = i.toString();
          }
          newFilterLabels.push(c.filter);
          if (c.active) {
            addQuery(c.filter.query);
          }
        });
      }
      if (filterLabels.categories) {
        filterLabels.categories.forEach((c) => {
          const category = metadata.getCategoryBySlug(c.categorySlug);
          newFilterLabels.push(categoryToFilterLabel(category));
          if (c.active) {
            const query = { id: category.id, categories: [category.id] };
            addQuery(query);
          }
        });
      }
      return newFilterLabels;
    }
    return metadata.getAllCategories().filter(c => !c.isSystem).map((category) => categoryToFilterLabel(category));
  }, [metadata, filterLabels]);

  const containsAnyFilters = useMemo(() => {
    for (const filterLabel of allFilterLabels) {
      if (filterLabel.query.id && has(filterLabel.query.id)) {
        return true;
      }
    }
    return false;
  }, [has, allFilterLabels, queries]);

  const activeTagBadges = useMemo(() => {
    return queries
      .filter((q) => q.id?.startsWith('tag:'))
      .map((q) => {
        const slug = q.tags?.[0] ?? q.id!.slice(4);
        const tag = metadata.tags.find((t) => t.slug === slug);
        const name = tag?.name ?? (slug.charAt(0).toUpperCase() + slug.slice(1));
        return { id: q.id!, name };
      });
  }, [queries, metadata.tags]);

  const childrenToDisplay = useMemo(() => {
    const children: FilterLabel[] = [];
    for (const filterLabel of allFilterLabels) {
      if (filterLabel.query.id && has(filterLabel.query.id) && filterLabel.children) {
        children.push(...filterLabel.children);
      }
    }
    return children;
  }, [allFilterLabels, has, queries]);

  function handleAddQuery(query: BalloonEventsQuery) {
    addQuery(query);
    trackMixpanel('FilterLabel', 'Add', {
      query,
    });
  }

  function handleRemoveQuery(query: BalloonEventsQuery) {
    removeQuery(query);
    trackMixpanel('FilterLabel', 'Remove', {
      query,
    });
  }
  function clearAllLabelFilters() {
    for (const filterLabel of allFilterLabels) {
      if (filterLabel.query.id && has(filterLabel.query.id)) {
        handleRemoveQuery(filterLabel.query);
      }
      // Also clear children filters
      if (filterLabel.children) {
        for (const child of filterLabel.children) {
          if (child.query.id && has(child.query.id)) {
            handleRemoveQuery(child.query);
          }
        }
      }
    }
  }
  const showAllButton = isMobile ? (
    <div className={'p-1 border-2 border-transparent mt-2 w-full'}>
      <button
        disabled={!containsAnyFilters}
        className={
          'self-center w-full inline-flex mt-[1rem] justify-center rounded-full border border-transparent px-4 py-2 text-sm font-medium text-[#F4E3F7] bg-[#BF2088] hover:text-[#BF2088] hover:bg-[#F4E3F7] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#BF2088] focus-visible:ring-offset-2'
        }
        onClick={clearAllLabelFilters}
      >
        Show all
      </button>
    </div>
  ) : containsAnyFilters ? (
    <div className={'p-1 border-2 border-transparent'}>
      <button
        className={
          'w-full rounded-full bg-balloon text-white px-4 py-1 text-[13px]'
        }
        onClick={clearAllLabelFilters}
      >
        Show all
      </button>
    </div>
  ) : null;

  const labels = allFilterLabels.map((c, i) => {
    return (
      <FilterLabelBadge
        key={i}
        language={language}
        className="cursor-pointer"
        inverted={
          !containsAnyFilters ||
          (containsAnyFilters && !!c.query.id && !has(c.query.id))
        }
        onClick={() => {
          if (c.query.id) {
            has(c.query.id)
              ? handleRemoveQuery(c.query)
              : handleAddQuery(c.query);
          }
        }}
        filterLabel={c}
      />
    );
  });

  const childrenLabels = childrenToDisplay.map((c, i) => {
    return (
      <FilterLabelBadge
        key={`child-${i}`}
        language={language}
        className="cursor-pointer"
        inverted={!c.query.id || !has(c.query.id)}
        onClick={() => {
          if (c.query.id) {
            has(c.query.id)
              ? handleRemoveQuery(c.query)
              : handleAddQuery(c.query);
          }
        }}
        filterLabel={c}
      />
    );
  });

  const tagBadgesRow = activeTagBadges.length > 0 ? (
    <div className={'w-full flex flex-row flex-wrap gap-1 mt-2 pt-2 pl-2 border-t border-gray-200'}>
      {activeTagBadges.map(({ id, name }) => (
        <span
          key={id}
          className={'inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-sm font-medium'}
          style={{ border: '1px solid rgb(191, 32, 136)', color: 'rgb(191, 32, 136)' }}
        >
          #{name}
          <button
            type="button"
            aria-label={`Remove tag ${name}`}
            className={'ml-1 leading-none hover:opacity-70 focus:outline-none'}
            onClick={() => {
              removeQueryById(id);
              trackMixpanel('FilterLabel', 'RemoveTag', { tagId: id, tagName: name });
            }}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  ) : null;

  if (isMobile) {
    return (
      <div
        className={'w-full flex flex-col justify-center items-center p-0 m-0'}
      >
        <div
          className={
            'w-full grid grid-cols-3 gap-1 p-0 flex-wrap [&_.badge-content]:w-full m-0'
          }
        >
          {labels}
        </div>
        {childrenToDisplay.length > 0 && (
          <div
            className={
              'w-full grid grid-cols-3 gap-1 p-0 flex-wrap [&_.badge-content]:w-full m-0 mt-2 pt-2 border-t border-gray-200'
            }
          >
            {childrenLabels}
          </div>
        )}
        {tagBadgesRow}
        {showAllButton}
      </div>
    );
  }
  return (
    <div className={'w-full flex flex-col px-2 py-2'}>
      <div className={'w-full flex flex-row justify-start items-center flex-wrap'}>
        {labels}
        {showAllButton}
      </div>
      {childrenToDisplay.length > 0 && (
        <div
          className={
            'w-full flex flex-row justify-start items-center flex-wrap mt-2 pt-2 border-t border-gray-200'
          }
        >
          {childrenLabels}
        </div>
      )}
      {tagBadgesRow}
    </div>
  );
});

/*

      <Badge
        className="cursor-pointer"
        style={{
          visibility: containsAnyFilters ? "visible" : "hidden" // contains any filter
        }}
        onClick={clear}
        color="black"
      >
        <span className="px-2 text-white">
          {translated("category_all")}
        </span>
      </Badge>

      */
